// ob-fix: Idempotent onboarding completion service with callable + fallback
import { doc, updateDoc, serverTimestamp, getDocs, query, collection, where, setDoc, getFirestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { db } from '../firebase';
import { FUNCTIONS_REGION } from '../config/flags';

interface OnboardingStatus {
  status: 'pending' | 'done';
  completedAt?: number;
}

export async function finishOnboarding(uid: string): Promise<{ ok: boolean }> {
  console.log('[go-live] ob-fix: Starting idempotent onboarding completion for uid:', uid); // ob-fix
  
  try {
    // Find user profile document
    const userQuery = query(collection(db, 'userProfiles'), where('uid', '==', uid));
    const userSnapshot = await getDocs(userQuery);
    
    if (userSnapshot.empty) {
      throw new Error('User profile not found');
    }
    
    const docId = userSnapshot.docs[0].id;
    const currentData = userSnapshot.docs[0].data();
    
    // Check if already completed (idempotent)
    if (currentData?.onboarding?.status === 'done') {
      console.log('[go-live] ob-fix: Onboarding already completed, returning success'); // ob-fix
      return { ok: true };
    }
    
    // Update onboarding status
    const onboardingStatus: OnboardingStatus = {
      status: 'done',
      completedAt: Date.now()
    };
    
    await updateDoc(doc(db, 'userProfiles', docId), {
      isOnboarded: true, // Legacy flag for backward compatibility
      onboarding: onboardingStatus,
      updatedAt: serverTimestamp()
    });
    
    console.log('[go-live] ob-fix: Firestore update successful'); // ob-fix
    return { ok: true };
    
  } catch (error) {
    console.error('[go-live] ob-fix: Firestore update failed:', error); // ob-fix
    throw error;
  }
}

// ob-fix: Callable approach with region support
export async function finishOnboardingCallable(): Promise<any> {
  try {
    const fns = getFunctions(getApp(), FUNCTIONS_REGION);
    const fn = httpsCallable(fns, "finishOnboarding");
    const res = await fn({});
    return res.data;
  } catch (error) {
    console.error('[go-live] ob-fix: Callable failed:', error);
    throw error;
  }
}

// ob-fix: Direct Firestore fallback
export async function finishOnboardingFallback(): Promise<void> {
  const auth = getAuth();
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("not-authenticated");
  
  console.log('[go-live] ob-fix: Using Firestore fallback for uid:', uid);
  
  // Use both userProfiles (current) and profiles (new) collections for compatibility
  const db = getFirestore();
  
  // IMPORTANT: Only update onboarding status fields, don't overwrite existing profile data
  const onboardingData = {
    onboarding: { 
      status: "done" as const, 
      completedAt: Date.now() 
    },
    isOnboarded: true, // Legacy compatibility
    updatedAt: new Date()
  };
  
  // ob-fix: Update userProfiles collection using UID as document ID (new consistent format)
  // Use merge: true to preserve existing data like firstName, lastName, displayName, etc.
  await setDoc(doc(db, "userProfiles", uid), onboardingData, { merge: true });
  
  // Also update profiles collection for compatibility
  await setDoc(doc(db, "profiles", uid), onboardingData, { merge: true });
}

export async function checkOnboardingStatus(uid: string): Promise<OnboardingStatus | null> {
  try {
    const userQuery = query(collection(db, 'userProfiles'), where('uid', '==', uid));
    const userSnapshot = await getDocs(userQuery);
    
    if (userSnapshot.empty) {
      return null;
    }
    
    const userData = userSnapshot.docs[0].data();
    return userData?.onboarding || { status: 'pending' };
    
  } catch (error) {
    console.error('❌ ob-fix: Error checking onboarding status:', error);
    return null;
  }
}
